// scheduler.ts
//
// Chọn ticket kế tiếp để dispatch. THUẦN: không I/O, không đọc mission-store,
// không Date.now() — supervisor.ts (P6 Agent C) lấy state từ MissionState rồi
// dựng `SchedulerProjection` truyền vào đây.
//
// Vì sao cần projection thay vì đọc thẳng Ticket: trạng thái runtime KHÔNG nằm
// trên ticket (ticket là file git, version bằng revision) mà nằm ở
// `MissionState.tickets` — map `ticketId -> status`. Hai nguồn tách nhau nên
// input phải mang cả hai.
//
// Thứ tự chọn (deterministic, không phụ thuộc thứ tự phần tử mảng đầu vào):
//   0. Phase 08 (Req 2, D3, J1): tầng ƯU TIÊN xét TRƯỚC slice — tầng 0 = kind
//      bootstrap (survey/plan) → tầng 1 = decision → tầng 2 = mọi kind còn
//      lại. Lý do: `decision` KHÔNG thuộc BOOTSTRAP_TICKET_KINDS (feature-
//      dispatch-gate-guard.ts) nên bị G0/G1 chặn như ticket feature — nếu
//      decision đứng trước bootstrap, một `redirect --text` lúc G0/G1 còn
//      pending khiến tick chọn decision ⇒ gate_blocked ⇒ `run` kết thúc,
//      survey/plan (thứ đẻ ra artifact để duyệt G0/G1) không bao giờ chạy
//      (deadlock). Nguồn kind bootstrap = BOOTSTRAP_KINDS của proposal-
//      change-types.ts (module thuần, KHÔNG kéo guard vào scheduler — pin
//      bằng nhau với guard qua bootstrap-kinds-pinned-across-layers.test.ts).
//   1. slice nào có ticket ID nhỏ nhất thì chạy trước — slice A xong mới sang
//      slice B (spec §5.2: "hoàn tất từng lát, không đan xen");
//   2. trong một slice: testcase → implement → test → các kind còn lại;
//      phase 06j (F-48): tầng 2 lọc qua cửa sổ RED trước khi sort
//      (scheduler-red-window-rule.ts) — testcase có neo không mở cửa sổ khi
//      việc khác còn chạy được, và khi test RED của nó còn trên nhánh thì chỉ
//      implement/fix làm xanh nó (cùng testcase đồng-tổ-tiên) được chạy.
//      Ứng viên rỗng ⇒ `undefined` (stall có lý do, xem
//      scheduler-red-window-stall-explainer.ts);
//   3. đồng hạng thì ID nhỏ hơn trước.
import type { z } from "zod";
import { resolveCommandAdapter } from "./command-adapter.js";
import { BOOTSTRAP_KINDS } from "./proposal-change-types.js";
import { filterRedWindowCandidates } from "./scheduler-red-window-rule.js";
import type { Ticket, TicketRuntimeStatusSchema } from "./schemas/ticket-schema.js";

/** Tái xuất từ ticket-schema.ts — một nguồn duy nhất cho tập trạng thái. */
export type TicketRuntimeStatus = z.infer<typeof TicketRuntimeStatusSchema>;

export interface SchedulerProjection {
  tickets: Ticket[];
  ticketStatus: Record<string, TicketRuntimeStatus>;
}

export interface DispatchSpec {
  kind: "dispatch";
  ticket: Ticket;
  allowedTools: string[];
}

export interface ContractErrorResult {
  kind: "contract_error";
  reason: string;
}

// Kind ngoài danh sách xếp sau cùng nhưng vẫn ổn định (theo ID).
const KIND_ORDER: readonly string[] = ["testcase", "implement", "test"];

function kindRank(kind: string): number {
  const index = KIND_ORDER.indexOf(kind);
  return index === -1 ? KIND_ORDER.length : index;
}

/** Tầng 0 = bootstrap (survey/plan), tầng 1 = decision, tầng 2 = còn lại. */
export function priorityTier(kind: string): number {
  if (BOOTSTRAP_KINDS.includes(kind)) return 0;
  if (kind === "decision") return 1;
  return 2;
}

function statusOf(
  ticketStatus: Record<string, TicketRuntimeStatus>,
  id: string,
): TicketRuntimeStatus {
  return ticketStatus[id] ?? "todo";
}

/**
 * Thứ hạng của slice = ID ticket nhỏ nhất trong slice đó. Dùng ID chứ không
 * dùng thứ tự xuất hiện vì mảng ticket có thể được đọc lại từ thư mục theo
 * thứ tự filesystem — không ổn định giữa các lần chạy.
 */
function sliceRankMap(tickets: Ticket[]): Map<string, string> {
  const ranks = new Map<string, string>();
  for (const ticket of tickets) {
    const current = ranks.get(ticket.slice_id);
    if (current === undefined || ticket.id < current) ranks.set(ticket.slice_id, ticket.id);
  }
  return ranks;
}

/** Ready = status "todo" và MỌI dependency đã "done". */
export function isReady(
  ticket: Ticket,
  ticketStatus: Record<string, TicketRuntimeStatus>,
): boolean {
  if (statusOf(ticketStatus, ticket.id) !== "todo") return false;
  // cancelled/superseded/failed/blocked KHÔNG thoả dependency — chỉ "done" mới
  // thoả (test 9: dep bị huỷ không mở khoá ticket phụ thuộc).
  return ticket.depends_on.every((dep) => statusOf(ticketStatus, dep) === "done");
}

/**
 * Ticket kế tiếp, hoặc `undefined` khi không có gì để chạy. Trả `undefined`
 * ngay khi đã có ticket "in_progress": autopilot v1 chạy MỘT phiên tại một
 * thời điểm (spec §5.2 — một worktree, một lease).
 */
export function nextReadyTicket(projection: SchedulerProjection): Ticket | undefined {
  const { tickets, ticketStatus } = projection;
  const busy = tickets.some((t) => statusOf(ticketStatus, t.id) === "in_progress");
  if (busy) return undefined;

  const ready = tickets.filter((t) => isReady(t, ticketStatus));
  const compare = dispatchOrder(tickets);
  const sorted = [...ready].sort(compare);
  const first = sorted[0];
  if (first === undefined || priorityTier(first.kind) < 2) return first;
  // Every ready ticket is tier 2 here (tiers sort first) ⇒ RED-window filter.
  return filterRedWindowCandidates(sorted, tickets, ticketStatus).candidates.sort(compare)[0];
}

/** Tầng → hạng slice → KIND_ORDER → ID (bước 0–3 ở header). */
function dispatchOrder(tickets: Ticket[]): (a: Ticket, b: Ticket) => number {
  const sliceRank = sliceRankMap(tickets);
  return (a, b) => {
    const tierDiff = priorityTier(a.kind) - priorityTier(b.kind);
    if (tierDiff !== 0) return tierDiff;
    const sliceA = sliceRank.get(a.slice_id) ?? a.id;
    const sliceB = sliceRank.get(b.slice_id) ?? b.id;
    if (sliceA !== sliceB) return sliceA < sliceB ? -1 : 1;
    const rankDiff = kindRank(a.kind) - kindRank(b.kind);
    if (rankDiff !== 0) return rankDiff;
    return a.id < b.id ? -1 : 1;
  };
}

/**
 * Phase chỉ được tiến khi MỌI ticket bắt buộc đã "done". Ticket đã
 * cancelled/superseded không còn bắt buộc (proposal đã gỡ nó khỏi phạm vi).
 */
export function canAdvancePhase(
  tickets: Ticket[],
  ticketStatus: Record<string, TicketRuntimeStatus>,
): boolean {
  return tickets.every((ticket) => {
    const status = statusOf(ticketStatus, ticket.id);
    if (status === "cancelled" || status === "superseded") return true;
    return status === "done";
  });
}

/**
 * Dựng spec để P2 spawn phiên. Adapter thiếu ⇒ `contract_error` (KHÔNG throw,
 * KHÔNG spawn): thiếu adapter là lỗi hợp đồng của planner, phải nổi lên cho
 * retry-policy đếm, chứ không được làm sập runner ở giữa vòng lặp.
 */
export function buildDispatchSpec(ticket: Ticket): DispatchSpec | ContractErrorResult {
  try {
    const adapter = resolveCommandAdapter(ticket.command_adapter);
    return { kind: "dispatch", ticket, allowedTools: [...adapter.allowedTools] };
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    return {
      kind: "contract_error",
      reason: `ticket ${ticket.id} khai command_adapter "${ticket.command_adapter}" không dựng được spec: ${detail}`,
    };
  }
}
